/**
 * Colonnes de la table des parcours (plan v3, phase 5).
 *
 * Écrites une fois, contre la projection `parcoursLigneValidator` du backend —
 * la même que servent `obtenirStatsCampagne` et `listerParcoursCampagneVue`.
 * Consommées par l'onglet Parcours de la fiche campagne et par l'écran
 * « Hors campagne ».
 *
 * La colonne `campagne` n'existe pas : on est déjà dans une campagne.
 */
import {
  Button,
  StatutParcoursBadge,
  filtreFacette,
  filtreRecherche,
  triOrdonne,
  type ColonneDataTable,
  type FacetteFiltre,
} from "@repo/ui";
import { dateCourte, motPluriel } from "@repo/utils";
import { Mail } from "lucide-react";
import type { Doc, Id } from "../../../convex/_generated/dataModel";
import {
  colonnesEpreuves,
  FACETTE_EPREUVES,
} from "./facettesEpreuves";

/** Une ligne de table, telle que la projette le backend. */
export type ParcoursLigne = {
  _id: Id<"parcoursEvaluations">;
  prenomCandidat?: string;
  nomCandidat?: string;
  emailCandidat?: string;
  titre: string;
  dateCreation: number;
  archive?: boolean;
  campagneId?: Id<"campagnes">;
  anonyme?: boolean;
  lienEnvoye?: boolean;
  statutCache?: "non_commence" | "en_cours" | "termine" | "archive";
  progression: {
    total: number;
    terminees: number;
    enCours: number;
    nonCommencees: number;
    pourcentage: number;
  };
  evaluationsResume: {
    typeEval: Doc<"evaluations">["typeEval"];
    template?: string;
    statut: Doc<"evaluations">["statut"];
  }[];
};

/** Ordre métier du statut, pour que le tri ne suive pas l'alphabet. */
const ORDRE_STATUT = [
  "non_commence",
  "en_cours",
  "termine",
  "archive",
] as const;

const LIBELLES_STATUT: Record<string, string> = {
  non_commence: "Non commencé",
  en_cours: "En cours",
  termine: "Terminé",
  archive: "Archivé",
};

/**
 * Facettes de la barre de filtres de la table des parcours (phase 6).
 *
 * Déclarées ici, à côté des colonnes qu'elles filtrent : un `id` de colonne
 * renommé sans sa facette donnerait un contrôle silencieusement inopérant.
 * Le type de contrôle (segments ou pill) se déduit de la cardinalité, la
 * `FilterBar` le décide seule.
 */
const FACETTE_STATUT: FacetteFiltre = {
  colonne: "statut",
  libelle: "Statut",
  libelles: LIBELLES_STATUT,
  ordre: ORDRE_STATUT,
};

const LIBELLES_LIEN: Record<string, string> = {
  envoye: "Envoyé",
  non_envoye: "Non envoyé",
};

/** Ordre métier : ce qui reste à faire d'abord. */
const ORDRE_LIEN = ["non_envoye", "envoye"] as const;

/** « Qui n'a pas encore reçu son lien ? » — la relance part de là. */
const FACETTE_LIEN: FacetteFiltre = {
  colonne: "lien",
  libelle: "Lien",
  libelles: LIBELLES_LIEN,
  ordre: ORDRE_LIEN,
};

/**
 * Facettes des tables de parcours, dans une campagne comme hors campagne.
 * L'épreuve reste filtrable dans une campagne : un parcours peut ne pas
 * porter toutes les épreuves de la campagne (candidat ajouté avant un
 * changement de config).
 */
export const FACETTES_PARCOURS: FacetteFiltre[] = [
  FACETTE_STATUT,
  FACETTE_LIEN,
  FACETTE_EPREUVES,
];

/** Recherche de la table des parcours, à passer à `barreFiltres`. */
export const RECHERCHE_PARCOURS = {
  colonne: "candidat",
  placeholder: "Nom, prénom, email…",
  libelle: "Rechercher un candidat",
} as const;

/**
 * Réglette d'épreuves (§A.5) : un segment par épreuve, teinté par les tokens
 * d'état. Remplace le couple `40 %` + `3/7` + barre de progression, qui disait
 * trois fois la même chose sans dire *quelle* épreuve reste à faire.
 */
function RegletteEpreuves({ ligne }: { ligne: ParcoursLigne }) {
  const { evaluationsResume, progression } = ligne;

  if (evaluationsResume.length === 0) {
    return <span className="text-sm text-muted-foreground">—</span>;
  }

  return (
    <div className="flex items-center gap-2">
      <div
        className="flex gap-0.5 flex-1"
        role="img"
        aria-label={`${progression.terminees} ${motPluriel(progression.terminees, "épreuve")} ${motPluriel(progression.terminees, "terminée")} sur ${progression.total}`}
      >
        {evaluationsResume.map((e, i) => {
          // Épreuve rendue (soumise ou analysée) / en cours / pas commencée.
          const teinte =
            e.statut === "soumis" ||
            e.statut === "analyse_en_cours" ||
            e.statut === "termine"
              ? "bg-etat-fait"
              : e.statut === "en_cours"
                ? "bg-etat-en-cours"
                : "bg-trait";
          return (
            <span
              key={`${e.typeEval}-${e.template ?? ""}-${i}`}
              className={`h-2 flex-1 rounded-pastille ${teinte}`}
              title={e.template ?? e.typeEval}
            />
          );
        })}
      </div>
      <span className="data text-xs text-muted-foreground shrink-0">
        {progression.terminees}/{progression.total}
      </span>
    </div>
  );
}

/**
 * Construit les colonnes. Les actions dépendent du portail, d'où le paramètre.
 */
export function colonnesParcours(options?: {
  /** Libellé du bouton d'action (défaut : « Voir »). */
  libelleAction?: string;
}): ColonneDataTable<ParcoursLigne>[] {
  const { libelleAction = "Voir" } = options ?? {};

  return [
    {
      // Prénom et nom fusionnés : deux bascules pour un seul concept,
      // c'était deux fois trop.
      id: "candidat",
      header: "Candidat",
      accessorFn: (l) =>
        `${l.prenomCandidat ?? ""} ${l.nomCandidat ?? ""}`.trim(),
      cell: ({ row }) => (
        <span className="font-medium">
          {`${row.original.prenomCandidat ?? ""} ${row.original.nomCandidat ?? ""}`.trim() ||
            "—"}
        </span>
      ),
      enableHiding: false,
      // La recherche est portée par cette colonne mais lit prénom, nom ET
      // email : taper un email trouve la ligne même colonne Email masquée.
      filterFn: filtreRecherche<ParcoursLigne>((l) => [
        l.prenomCandidat,
        l.nomCandidat,
        l.emailCandidat,
      ]),
      meta: { libelle: "Candidat", largeur: "20%" },
    },
    {
      id: "email",
      header: "Email",
      accessorFn: (l) => l.emailCandidat ?? "",
      cell: ({ row }) => (
        <div className="truncate">{row.original.emailCandidat ?? "—"}</div>
      ),
      meta: { libelle: "Email", largeur: "20%" },
    },
    {
      id: "lien",
      header: "Lien",
      // Clé métier, pas un booléen numérique : c'est elle que la facette
      // affiche et que l'URL transporte. L'ordre passe par `sortFn`.
      accessorFn: (l) => (l.lienEnvoye ? "envoye" : "non_envoye"),
      filterFn: filtreFacette<ParcoursLigne>(),
      sortFn: triOrdonne<ParcoursLigne>(ORDRE_LIEN),
      cell: ({ row }) =>
        row.original.lienEnvoye ? (
          <Mail className="h-4 w-4 text-etat-fait" aria-label="Lien envoyé" />
        ) : (
          <Mail
            className="h-4 w-4 text-muted-foreground/30"
            aria-label="Lien non envoyé"
          />
        ),
      meta: { libelle: "Lien", largeur: "7%" },
    },
    {
      id: "progression",
      header: "Progression",
      // Tri sur le pourcentage, pas sur le rendu.
      accessorFn: (l) => l.progression.pourcentage,
      cell: ({ row }) => <RegletteEpreuves ligne={row.original} />,
      meta: { libelle: "Progression", largeur: "17%" },
    },
    ...colonnesEpreuves<ParcoursLigne>((l) => l.evaluationsResume),
    {
      id: "statut",
      header: "Statut",
      // Valeur d'accès = la clé métier, pas son rang : c'est elle que les
      // facettes affichent et que l'URL transporte. L'ordre passe par `sortFn`.
      accessorFn: (l) => l.statutCache ?? "non_commence",
      sortFn: triOrdonne<ParcoursLigne>(ORDRE_STATUT),
      filterFn: filtreFacette<ParcoursLigne>(),
      cell: ({ row }) => (
        <StatutParcoursBadge statut={row.original.statutCache ?? "non_commence"} />
      ),
      meta: { libelle: "Statut", largeur: "12%" },
    },
    {
      id: "dateCreation",
      header: "Créé le",
      accessorFn: (l) => l.dateCreation,
      cell: ({ row }) => (
        <span className="data">{dateCourte(row.original.dateCreation)}</span>
      ),
      meta: { libelle: "Date de création", largeur: "10%" },
    },
    {
      id: "actions",
      header: "",
      enableSorting: false,
      enableHiding: false,
      // La ligne entière est déjà un lien : ce bouton est un affordance
      // visuel, retiré du parcours de tabulation pour ne pas doubler la cible.
      cell: () => (
        <Button variant="ghost" size="sm" tabIndex={-1} aria-hidden>
          {libelleAction}
        </Button>
      ),
      meta: { largeur: "8%", aDroite: true },
    },
  ];
}
